import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { MessageCircle, ArrowRight, ShieldCheck } from "lucide-react";
import { waHref } from "@/lib/wa";
import { scrollToSection } from "./Navbar";

const MaskedWords = ({ text }) => (
  <h1 className="font-display text-4xl font-extrabold leading-[1.02] tracking-tighter text-brand-dark sm:text-5xl lg:text-6xl">
    <span className="sr-only">Sakofa Properti — Perumahan Type 36 Cluster di Pontianak Utara oleh PT. Cahaya Sakofa Mulia. </span>
    {text.split(" ").map((word, i) => (
      <span key={i} className="mask-line mr-[0.28em]">
        <motion.span
          className="inline-block"
          initial={{ y: "110%" }}
          animate={{ y: 0 }}
          transition={{ duration: 0.9, delay: 0.15 + i * 0.07, ease: [0.22, 1, 0.36, 1] }}
        >
          {word === "Keluarga" ? <span className="text-brand">{word}</span> : word}
        </motion.span>
      </span>
    ))}
  </h1>
);

export default function Hero({ content }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);
  const cta = waHref(content);

  return (
    <section id="beranda" ref={ref} className="hero-spotlight bg-fade-hero bg-grid-blue relative overflow-hidden pt-16" data-testid="hero-section">
      <div className="mx-auto grid max-w-7xl items-center gap-12 px-4 pb-16 pt-12 sm:px-6 lg:grid-cols-2 lg:pb-24 lg:pt-20">
        <div>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 rounded-full border border-brand/20 bg-brand-mist px-4 py-1.5"
            data-testid="hero-badge"
          >
            <ShieldCheck className="h-3.5 w-3.5 text-brand" />
            <span className="text-[11px] font-semibold uppercase tracking-[0.2em] text-brand">
              {content.hero.badge}
            </span>
          </motion.div>

          <div className="mt-6">
            <MaskedWords text={content.hero.headline} />
          </div>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.7 }}
            className="mt-6 max-w-xl text-base leading-relaxed text-slate-600"
            data-testid="hero-subheadline"
          >
            {content.hero.subheadline}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.85 }}
            className="mt-8 flex flex-wrap items-center gap-4"
          >
            <button
              onClick={() => scrollToSection("#perumahan")}
              data-testid="hero-cta-lihat-perumahan"
              className="inline-flex items-center gap-2 rounded-full bg-brand px-7 py-3.5 text-sm font-semibold text-white transition-all duration-200 hover:bg-brand-deep active:scale-95"
            >
              Lihat Perumahan
              <ArrowRight className="h-4 w-4" />
            </button>
            <a
              href={cta}
              target={cta.startsWith("http") ? "_blank" : undefined}
              rel="noopener"
              data-testid="hero-cta-hubungi-marketing"
              className="inline-flex items-center gap-2 rounded-full border-2 border-brand-dark/10 bg-white px-7 py-3 text-sm font-semibold text-brand-dark transition-colors duration-200 hover:border-brand hover:text-brand active:scale-95"
            >
              <MessageCircle className="h-4 w-4" />
              Hubungi Marketing
            </a>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
          className="relative"
        >
          <div className="overflow-hidden rounded-3xl shadow-[0_24px_60px_-16px_rgb(0,86,179,0.25)]" data-testid="hero-image-frame">
            <motion.img
              src={content.hero.image}
              alt="Perumahan modern Type 36 konsep cluster PT. Cahaya Sakofa Mulia"
              style={{ y: imgY, scale: imgScale }}
              className="aspect-[4/3] w-full object-cover"
              loading="eager"
            />
          </div>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 1.1 }}
            className="absolute -bottom-5 -left-3 rounded-2xl border border-slate-100 bg-white px-5 py-4 shadow-[0_8px_30px_rgb(0,0,0,0.08)] sm:-left-8"
          >
            <p className="font-display text-2xl font-bold text-brand">Type 36</p>
            <p className="text-xs font-medium uppercase tracking-[0.15em] text-slate-500">Konsep Cluster</p>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
